import { AccountBalanceOutlined, AddCircleOutline, AutorenewRounded } from '@mui/icons-material'
import clsx from 'clsx'
import type Stripe from 'stripe'
import { useDispatch } from 'react-redux'
import { Unsubscribe, doc, onSnapshot } from 'firebase/firestore'
import React, { useEffect, useState } from 'react'

import { VideoModal } from '../../modals/VideoModals'
import ViewsLoader from '../../../../../loader/ViewsLoader'
import OrganizerStripeTransactions from '../../../../../modals/OrganizerStripeTransactions'
import FormHeader from '../../../../../ui/form/form-header/FormHeader'
import { CurrencyTooltip } from '../../components/CurrencyTooltip'

import helpers from '../../../../../../commonHelpers/helpers'
import useToasterHelper from '../../../../../../helpers/ToasterHelper'
import useProfileHook from '../../../../../../hooks/users/competitor/profile/useProfileHook'
import useFinances from '../../../../../../hooks/useFinances'

import FirebaseApp from '../../../../../../services/firebaseApp'
import { httpService } from '../../../../../../services/httpService'
import formFunctions from '../../../../../ui/form/form-functions/formFunctions'

import { CONST } from '../../../../../../const/const'
import { MESSAGES_CONST } from '../../../../../../const/messages-const'
import { MODAL_CONSTS } from '../../../../../../const/modal-const'
import { CountryList } from '../../../../../../fakeData/countryList'

import { IHandleModal } from '../../../../../../layout/mainlayout/MainLayout'
import { UserModel } from '../../../../../../models/users/user.model'

import { useAppSelector } from '../../../../../../store/hooks'
import {
  selectProfileData,
  selectUserId,
  setProfileDetails,
} from '../../../../../../store/user/userSlice'
import {
  selectConnectAccountDetail,
  selectConnectAllPayouts,
  selectConnectAvailableBalance,
  selectPayoutsTotal,
} from '../../../../../../store/finances/financesSlice'
import { currencies } from '../../../../../../fakeData/currencies'
import { ICurrency } from '../../../../../../models/event-payment-settings/event-payment-settings.interface'

// Types
export type IConnectAllPayout = Stripe.Payout & { accountNumber: string }

type Props = {
  handleModal?: IHandleModal['handleModal']
}

const ProfileRootPageFinanceTab = (props: Props) => {
  // Hooks and vars
  const dispatch = useDispatch()

  const toasterFunctions = useToasterHelper()
  const { updateUserDetails } = useProfileHook()
  const { getTransactions, transactionLoading } = useFinances()

  const userId = useAppSelector(selectUserId)
  const profileData = useAppSelector(selectProfileData)
  const connectAccountDetail = useAppSelector(selectConnectAccountDetail)
  const connectAvailableBalance = useAppSelector(selectConnectAvailableBalance)
  const connectAllPayouts = useAppSelector(selectConnectAllPayouts)
  const payoutsTotal = useAppSelector(selectPayoutsTotal)

  const [creating, setCreating] = useState(false)
  const [limit, setLimit] = useState<number>(25)
  const hasMore = useState<boolean>(false)[0]
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false)

  const currencyCode = connectAccountDetail[0]?.currency?.toUpperCase()
  const currencySymbol = currencies[currencyCode as ICurrency]?.symbol

  const formStyles = formFunctions.getFormStyles({ noPaddingRt: true })

  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null

    if (userId) {
      unsubscribe = onSnapshot(
        doc(FirebaseApp.firestore, CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.USERS.NAME, userId),
        (doc) => {
          if (doc.exists()) {
            let user = UserModel.fromFirestoreDoc(doc)
            dispatch(setProfileDetails(user))
          }
        }
      )
    }

    return () => {
      unsubscribe?.()
    }
  }, [dispatch, userId])

  useEffect(() => {
    getTransactions().then()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, profileData.userStripeAccountId])

  const fetMoreData = () => {
    if (hasMore) setLimit(limit + 25)
  }

  const onTransferButtonClick = () => {
    props?.handleModal?.(true, MODAL_CONSTS.NEW_TRANSFER, {
      stripeConnectAccountId: profileData?.userStripeAccountId,
      last4: connectAccountDetail[0]?.last4,
      connectAvailableBalance: connectAvailableBalance?.[0] ? connectAvailableBalance : [],
      connectAccountDetail: connectAccountDetail?.[0] ? connectAccountDetail : [],
    })
  }

  const goToLink = (link: string) => {
    window.open(link, '_blank')
  }

  const updateProfile = async (accountId: string) => {
    try {
      const res = await updateUserDetails({
        userStripeAccountId: accountId,
        userStripeAccountStatus: 'pending',
      })

      if (res.status) {
        toasterFunctions.success({
          message: 'Stripe account created successfully',
        })
      }
    } catch (error) {
      toasterFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      helpers.logger({
        isError: true,
        message: error,
      })
    }
  }

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * @info Checks whether all the details that are needed for account creation are valid or not
   * @return True if all details are valid else false
   */
  const validateDetailsForStripe = (): Boolean => {
    let valueToReturn = true
    let nationality = profileData?.userNationality

    if (profileData?.userNationality)
      nationality = CountryList.find((curr) => curr.value === profileData?.userNationality)?.label

    if (!nationality) {
      valueToReturn = false
      toasterFunctions.error({
        message: MESSAGES_CONST.COUNTRY_NOT_FILLED,
      })
    }

    return valueToReturn
  }

  const handleCreateConnectAccount = async (userStripeAccountId: string | null) => {
    let id: string | null = null
    let url: string | null = null

    const creatAccount = !userStripeAccountId
    const userEmail = profileData?.userEmail
    let userNationality = profileData?.userNationality

    const nationality = CountryList.filter((data) => {
      return data.value === userNationality
    })
    userNationality = nationality.length ? nationality[0].value : profileData?.userNationality

    if (!validateDetailsForStripe()) return false

    setCreating(true)

    try {
      // Create account
      if (creatAccount) {
        const result: any = await httpService({
          url: 'create_stripe_account',
          method: 'POST',
          data: {
            email: userEmail,
            country: userNationality,
          },
        })

        id = result?.data?.id ?? null
        url = result.accountLink.url

        if (id) await updateProfile(id)

        if (url) goToLink(url)
      }
      // Manage account
      else {
        props.handleModal?.(true, MODAL_CONSTS.MANAGE_STRIPE_CONNECT_ACCOUNT, {
          handleModal: props.handleModal,
        })
      }
    } catch (error: any) {
      let message = MESSAGES_CONST.SOMETHING_WENT_WRONG

      if (error?.response?.data?.message) message = error?.response?.data?.message

      toasterFunctions.error({ message })
    } finally {
      setCreating(false)
    }
  }

  const getConnectBankAccountHtml = () => {
    let isCreated = false
    let userStripeAccountId = profileData?.userStripeAccountId ?? null

    if (userStripeAccountId) isCreated = true

    return (
      <>
        <div className="flex flex-col md:flex-row gap-4">
          <button
            type="button"
            onClick={() => {
              handleCreateConnectAccount(userStripeAccountId)
            }}
            className="w-full md:w-[450px] border border-solid border-SeabiscuitGray500ThemeColor text-[#122B46] py-[15px] h-[51px] px-6 rounded-xl text-nr text-left flex items-center justify-between"
          >
            {creating ? (
              <>
                <span className="mr-2 flex items-center justify-between w-full">
                  {isCreated ? 'Redirecting you to stripe...' : 'Connecting to your account...'}
                  <AutorenewRounded fontSize="small" className="animate-spin ml-auto" />
                </span>
              </>
            ) : (
              <>
                {isCreated
                  ? `Connected account ending in: ***${connectAccountDetail[0]?.last4 ?? '-****'}`
                  : 'Connect your bank account to receive funds'}
                {isCreated ? (
                  <img src="/assets/og_icons/Edit.svg" alt="editIcon" />
                ) : (
                  <AddCircleOutline />
                )}
              </>
            )}
          </button>

          {isCreated ? (
            <>
              {profileData?.userStripeAccountStatus ? (
                <span
                  className={clsx(
                    'text-nr h-[51px] font-medium rounded-xl capitalize px-3 flex justify-center items-center',
                    profileData?.userStripeAccountStatus === 'connected'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  )}
                >
                  {profileData?.userStripeAccountStatus}
                </span>
              ) : null}
            </>
          ) : null}
        </div>

        {profileData?.userStripeAccountStatus === 'pending' &&
        !!profileData?.userConnectAccountStatusMessage ? (
          <label className="text-nr h-fit mt-[10px] text-[#122B46] w-full">
            {profileData?.userConnectAccountStatusMessage}
          </label>
        ) : null}
      </>
    )
  }

  return (
    <>
      <FormHeader
        title={'Finances'}
        description={'Transfer funds to your connected bank account'}
        headerButtonsContainer={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="items-center w-[125px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn"
              onClick={() => setOpenVideoModal(true)}
            >
              More info
            </button>
            <button
              type="button"
              disabled={transactionLoading}
              onClick={onTransferButtonClick}
              className="items-center w-[150px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn min-w-[150px]"
            >
              New Transfer
            </button>
          </div>
        }
      />

      <div className={formStyles.className} style={formStyles.styles}>
        {transactionLoading ? (
          <div className="w-full flex justify-center items-center">
            <ViewsLoader size="xl" color="red" />{' '}
          </div>
        ) : (
          <>
            {/* Account details */}
            <div className="AccountEmail flex flex-wrap mb-8">
              <label className="text-nr md:mr-8 w-full md:w-1/4 h-fit mt-[10px] text-[#122B46]">
                <AccountBalanceOutlined className="mr-2" />
                Bank account
              </label>
              <div className="flex flex-col gap-2 mt-3 md:mt-0">{getConnectBankAccountHtml()}</div>
            </div>

            <hr className="my-4"></hr>

            {/* Fund Details */}
            <div className="AccountEmail mt-8 mb-3 flex flex-wrap items-center">
              <label className="text-nr md:mr-8 mb-3 md:mb-0  w-full md:w-1/4 flex items-center text-[#122B46]">
                <img className="mr-2" src="/assets/og_icons/MoneyBag5.svg" alt="MoneyBagIcon" />
                <p>
                  Funds available for transfer (<CurrencyTooltip currencyCode={currencyCode} />)
                </p>
              </label>
              <div className="flex flex-col w-[450px]">
                <span
                  className={clsx(
                    'rounded-xl py-[15px] h-[51px] px-6',
                    connectAvailableBalance?.[0] && connectAvailableBalance[0]?.amount
                      ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5  border border-SeabiscuitMainThemeColor/5 font-bold '
                      : 'bg-[white] border border-[#D3DAEE] text-[#122B46]'
                  )}
                >
                  {currencySymbol}
                  {connectAvailableBalance?.[0]
                    ? (connectAvailableBalance[0].amount / 100).toFixed(2)
                    : 0}
                </span>
              </div>
            </div>
            {/* Historic transfer Details */}
            <div className="AccountEmail mb-3 flex flex-wrap items-center">
              <label className="text-nr  md:mr-8  mb-3 md:mb-0  w-full md:w-1/4 flex items-center text-[#122B46]">
                <img className="mr-2" src="/assets/og_icons/MoneyBag5.svg" alt="MoneyBagIcon" />
                <p>
                  Historic transfer total (<CurrencyTooltip currencyCode={currencyCode} />)
                </p>
              </label>
              <div className="flex flex-col w-full md:w-[initial]">
                <span className="border border-[#D3DAEE] w-full md:w-[450px] text-[#122B46] py-[15px] h-[51px] px-6 rounded-xl">
                  {currencySymbol}
                  {payoutsTotal}
                </span>
              </div>
            </div>

            {/* Previous number of transfers Details */}
            <div className="AccountEmail mb-3 flex flex-wrap items-center">
              <label className="text-nr md:mr-8  mb-3 md:mb-0  w-full md:w-1/4 flex items-center text-[#122B46]">
                <img className="mr-2" src="/assets/og_icons/MoneyBag5.svg" alt="MoneyBagIcon" />
                Previous number of transfers
              </label>
              <div className="flex flex-col w-full md:w-[initial]">
                <span className="border border-[#D3DAEE] w-full md:w-[450px] text-[#122B46] py-[15px] h-[51px] px-6 rounded-xl">
                  {connectAllPayouts.length}
                </span>
              </div>
            </div>

            <hr className="my-8"></hr>

            {/* Transactions History */}

            <div className="">
              <h4 className="text-[#122B46] font-bold my-4">Transaction History</h4>
            </div>

            <OrganizerStripeTransactions
              handleModal={props.handleModal}
              hasMore={hasMore}
              fetMoreData={fetMoreData}
              loading={transactionLoading}
              stripeConnectAccountId={profileData?.userStripeAccountId ?? ''}
              connectAccountDetail={connectAccountDetail}
              currencyCode={currencyCode}
            />
          </>
        )}
      </div>
      {openVideoModal && (
        <VideoModal
          open={openVideoModal}
          url={'https://google.com'}
          onClose={() => setOpenVideoModal(false)}
        />
      )}
    </>
  )
}

export default ProfileRootPageFinanceTab
